import React, { useEffect, useState } from "react";
import { Box, ButtonBase, Tooltip } from "@mui/material";
import { keyframes } from "@mui/system";
import { hasAnyFleetBotAccess } from "./privileges";

const ACCENT = "#1f9d8a";
const BOT = "#5b6670";

const pulse = keyframes`
  0%   { transform: scale(1);   opacity: .7; }
  70%, 100% { transform: scale(1.55); opacity: 0; }
`;
const float = keyframes`
  50% { transform: translateY(-2px); }
`;
const blink = keyframes`
  0%, 92%, 100% { transform: scaleY(1); }
  95%           { transform: scaleY(.1); }
`;
const glow = keyframes`
  50% { opacity: .25; }
`;

function RobotIcon({ open }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 34 34"
      aria-hidden="true"
      sx={{
        width: 26,
        height: 26,
        animation: open ? "none" : `${float} 3s ease-in-out infinite`,
        "& .eye": {
          fill: "#fff",
          transformBox: "fill-box",
          transformOrigin: "center",
          animation: `${blink} 4s infinite`,
        },
        "& .eye.r": { animationDelay: ".05s" },
        "& .tip": { fill: ACCENT, animation: `${glow} 1.6s ease-in-out infinite` },
      }}
    >
      <line
        x1="17"
        y1="8"
        x2="17"
        y2="4"
        stroke={BOT}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle className="tip" cx="17" cy="3.5" r="2" />
      <rect x="4" y="8" width="26" height="22" rx="7" fill={BOT} />
      <rect className="eye" x="10" y="14" width="4" height="6" rx="2" />
      <rect className="eye r" x="20" y="14" width="4" height="6" rx="2" />
      <path
        d="M13 25h8"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </Box>
  );
}

export default function ChatbotButton({ label = "Ask FleetBot" }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onState = (event) => {
      setOpen(!!event?.detail?.open);
    };
    window.addEventListener("fleetbot:state", onState);
    return () => window.removeEventListener("fleetbot:state", onState);
  }, []);

  if (!hasAnyFleetBotAccess()) return null;

  return (
    <Tooltip title={label} placement="bottom" arrow>
      <ButtonBase
        onClick={() => {
          window.dispatchEvent(new CustomEvent("fleetbot:toggle"));
        }}
        aria-label={open ? "Close chat" : label}
        aria-expanded={open}
        focusRipple
        sx={{
          flex: "0 0 auto",
          position: "relative",
          width: 34,
          height: 34,
          borderRadius: "50%",
          bgcolor: "background.paper",
          border: `1.5px solid ${BOT}`,
          boxShadow: "0 2px 8px rgba(91, 102, 112, 0.22)",
          transition: "transform .25s cubic-bezier(.34,1.56,.64,1)",
          "&:hover": { transform: "scale(1.08)" },
          "&:active": { transform: "scale(.94)" },
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            border: `2px solid ${ACCENT}`,
            animation: open ? "none" : `${pulse} 2.4s ease-out infinite`,
            opacity: open ? 0 : undefined,
          },
          "@media (prefers-reduced-motion: reduce)": {
            "&::before": { animation: "none" },
          },
        }}
      >
        <RobotIcon open={open} />
      </ButtonBase>
    </Tooltip>
  );
}
